/**
 *
 * CommunicationForm
 *
 */

import React, { memo, useCallback, useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import moment from 'moment';

import './style.scss';

import { Form } from 'antd';

// Communication components
import {
  AudiencePreview
} from 'components/Communications';

// components
import DateField from 'components/Forms/Fields/DateField';
import HiddenField from 'components/Forms/Fields/HiddenField';
import DynamicMultipleSelect from 'components/Forms/Fields/DynamicMultipleSelect';
import RichEditorField from 'components/Forms/Fields/RichEditorField';
import ThumbnailUpload from 'components/ThumbnailUpload';

// helper for forms
import { generateAudienceIncludes } from 'utils/helpers/helper.generateData';
// init forms
import { initFormsCommunication } from 'utils/forms/forms.init';
// utils
import {
  prepearFormValue,
  prepearFormValueRemoveHiddenChain,
  setFormValues
} from 'utils/forms/forms.utils';

function CommunicationForm({
  mode,
  method,
  form,
  initialValues,
  name,
  dataSource,
  communicationDetail,
  disabeled,
  // actions
  handleSubmit,
  handleChange,
  handleValuesChange,
  // default
  className,
  ...rest
}) {
  const childClassNames = classNames('custom-form-wrapper', className);

  const onlyRead = mode == 'onlyRead';
  const isEdit = mode == 'edit' || mode == 'new';

  useEffect(() => { 
    if (!!form && !!communicationDetail) {
      const initForm = initFormsCommunication();
      const itemObj = {
        ...communicationDetail,
        'description-editor': communicationDetail?.description || '',
      };
      setFormValues(form, initForm, itemObj);
    }
  }, []);

  const onFinish = values => {
    const sendingData = prepearFormValue(dataSource, values);
    const sendingDataClear = prepearFormValueRemoveHiddenChain(
      dataSource,
      sendingData,
    );
    handleSubmit(sendingDataClear);
  };

  const onFinishFailed = errorInfo => {
    console.error('Failed:', errorInfo);
  };

  const dataSourceHidden = [
    {
      label: '',
      name: 'audience',
      required: true,
      value: communicationDetail?.audience || {},
      type: 'hidden',
      col: 24,
    },
    {
      label: 'Title',
      name: 'title',
      required: true,
      value: communicationDetail?.title || '',
      type: 'hidden',
      col: 24,
    },
    {
      label: '',
      name: 'description',
      required: true,
      value: '',
      type: 'hidden',
      col: 24,
      placeholder: 'Descriptif des communications',
    },
    {
      label: '',
      name: 'status',
      required: true,
      value: 'draft',
      type: 'hidden',
      col: 24,
    },
    {
      label: '',
      name: 'types',
      required: ['no-type'],
      value: null,
      type: 'hidden',
      col: 24,
    },
  ];

  const audienseSelect = useMemo(() => {
    const onlyReadMode = mode == 'onlyRead';
    return (
      <DynamicMultipleSelect
        key={`axios-select-multiple-communication`}
        index={`axios-select-multiple-communication`}
        mode={mode}
        method={method}
        onlyRead={onlyReadMode}
        form={form}
        data={{
          label: 'Audience',
          name: 'audience-select',
          endponit: `communications/audience/by/schools,students,courses,classes,audiences,users`,
          endponitSend: {
            value: '',
            include:
              generateAudienceIncludes(communicationDetail) || [],
            firstLoad: true,
          },
          displayLabel: true,
          required: true,
          type: 'axios-select-multiple',
          hasModeView: true,
          hiddenChain: 'audience',
          col: 24,
          method: 'POST',
          groupped: true,
          optionLabelProp: 'title', // 'label
          value: [],
          className: 'axios-select',
          mode: 'multiple',
          isSearch: true,
          placeholder: 'Selectionner le(s) audience',
          showAsTag: true,
          onSearch: input => {},
        }}
      />
    )
  }, [form, mode, method, communicationDetail?._id])

  return (
    <div className={childClassNames}>
      <Form
        form={form}
        initialValues={!!initialValues && initialValues || {}}
        name={name}
        className="custom-form"
        labelCol={{ span: 24 }}
        wrapperCol={{ span: 24 }}
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        onFieldsChange={handleChange}
        onValuesChange={handleValuesChange}
        autoComplete="off"
      >
        <div className="communication-body-header">
          <div className="communication-body-header__fileds">
            {audienseSelect}

            {(isEdit || onlyRead) && (
              <DateField
                key={`date-communication`}
                data={{
                  label: 'Date butoir de lecture ou réponse (optionel)',
                  onlyReadLabel: 'Date butoir',
                  name: 'deadlineDate',
                  required: false,
                  hasModeView: true,
                  value: null,
                  type: 'date',
                  col: 24,
                }}
                index={`date-communication`}
                onlyRead={onlyRead}
                className={`${onlyRead ? ' only-read' : ''}`}
                disabledDate={current => {
                  // Can not select days before today and today
                  return current && current < moment().endOf('day');
                }}
              />
            )}
          </div>
          {(isEdit || !!communicationDetail?.thumbnail && onlyRead) && (
            <div className="communication-body-header__upload">
              <ThumbnailUpload
                name="thumbnail"
                mode={mode}
                method={method}
                wrapForm={true}
                label={`Image à la une`}
                onlyRead={onlyRead}
                value={communicationDetail?.thumbnail}
                onRemove={({status}) => {
                  if (status == 200) {
                    form.setFieldsValue({
                      thumbnail: null
                    });
                    !!handleValuesChange && handleValuesChange({
                      thumbnail: null
                    }, form?.getFieldsValue())
                  }
                }}
                onResult={imageUrl => {
                  if (!!imageUrl) {
                    form.setFieldsValue({
                      thumbnail: imageUrl
                    });
                    !!handleValuesChange && handleValuesChange({
                      thumbnail: imageUrl
                    }, form?.getFieldsValue())
                  }
                }}
              />
            </div>  
          )}
        </div>

        <RichEditorField
          key={`rich-editor-communication`}
          mode={mode}
          form={form}
          data={{
            label: '',
            name: 'description-editor',
            hiddenChain: 'description',
            required: false,
            value: communicationDetail?.description || '',
            hasModeView: false,
            type: 'rich-editor',
            type_editor: 'simple',
            col: 24,
            placeholder: 'Descriptif des communications',
            onChange: text => {
              //  console.log({ text });
            },
          }}
          index={`rich-editor-communication`}
          method={method}
        />
        {dataSourceHidden?.map(
          (item, index) =>
            item && (
              <HiddenField
                key={`hidden-${index}`}
                data={item}
                index={index}
              />
            ),
        )}
      </Form>
    </div>
  );
}

CommunicationForm.defaultProps = {
  disabeled: false,
  method: 'update',
  name: 'basic'
};

CommunicationForm.propTypes = {
  disabeled: PropTypes.oneOfType([
    PropTypes.bool.isRequired,
    PropTypes.string.isRequired,
  ]),
};

export default memo(CommunicationForm);